import type { PurchaseMoneyRecord } from '@mentora/data-access'
import type { ReactNode } from 'react'
import type { PriceFormatter } from '../price-formatter'
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'
import { useStatusLabel } from './use-status-label'

// The top of a transaction's details: who bought, and what, when and how —
// the facts an admin reads before the money record and before refunding.

function Field({ label, children, mono = false }: { label: string, children: ReactNode, mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-xs text-muted-foreground sm:text-sm">{label}</dt>
      <dd className={`text-sm break-words text-foreground sm:text-right ${mono ? 'font-mono tabular-nums break-all' : ''}`}>{children}</dd>
    </div>
  )
}

export function BuyerCard({ buyer }: { buyer: PurchaseMoneyRecord['buyer'] }) {
  const tb = useTranslations('transactions.details.buyer')
  const format = useFormatter()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{tb('title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {buyer
          ? (
              <dl className="divide-y divide-border/60">
                <Field label={tb('name')}>{buyer.name ?? '—'}</Field>
                <Field label={tb('email')}>
                  {buyer.email ? <a href={`mailto:${buyer.email}`} className="underline-offset-4 hover:underline">{buyer.email}</a> : '—'}
                </Field>
                {buyer.phone && <Field label={tb('phone')}>{buyer.phone}</Field>}
                {buyer.location && <Field label={tb('location')}>{buyer.location}</Field>}
                {buyer.cpf && <Field label={tb('cpf')} mono>{buyer.cpf}</Field>}
                {buyer.crm && <Field label={tb('crm')} mono>{buyer.crm}</Field>}
                {buyer.memberSince && <Field label={tb('since')}>{format.dateTime(new Date(buyer.memberSince), { dateStyle: 'medium' })}</Field>}
                <Field label={tb('id')} mono>{buyer.id}</Field>
              </dl>
            )
          : <p className="text-sm text-muted-foreground">{tb('missing')}</p>}
      </CardContent>
    </Card>
  )
}

export function PurchaseCard({ record, formatPrice }: { record: PurchaseMoneyRecord, formatPrice: PriceFormatter }) {
  const t = useTranslations('transactions.details.purchase')
  const format = useFormatter()
  const statusLabel = useStatusLabel()
  const date = (iso: string | null) => iso ? format.dateTime(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' }) : '—'
  const { item } = record

  const failureLabel: Record<string, string> = {
    abandoned: t('failure.abandoned'),
    expired: t('failure.expired'),
    provider_failed: t('failure.provider_failed'),
    cancelled_by_admin: t('failure.cancelled_by_admin'),
  }
  const eligibilityLabel: Record<string, string> = {
    general: t('eligibility.general'),
    ascps_member: t('eligibility.ascps_member'),
    partner_member: t('eligibility.partner_member'),
    membership_bundle: t('eligibility.membership_bundle'),
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{t('title')}</CardTitle>
          <Badge variant="outline">{record.kind === 'course' ? t('kindCourse') : t('kindEvent')}</Badge>
          <Badge variant="secondary" data-testid="transaction-status">{statusLabel(record.status)}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border/60">
          <Field label={t('item')}>{item?.title ?? t('itemMissing')}</Field>
          {item?.kind === 'course' && item.organizationName && <Field label={t('organization')}>{item.organizationName}</Field>}
          {item?.kind === 'event' && (
            <>
              <Field label={t('eventDate')}>{date(item.startAt)}</Field>
              <Field label={t('tier')}>{`${item.tierName} · ${eligibilityLabel[item.tierEligibility] ?? item.tierEligibility}`}</Field>
              {item.entryCode && <Field label={t('entryCode')} mono>{item.entryCode}</Field>}
              {item.partner && (
                <Field label={t('partnerSociety')}>
                  {item.partner.society}
                  {item.partner.proofUrl && (
                    <>
                      {' · '}
                      <a href={item.partner.proofUrl} target="_blank" rel="noreferrer" className="underline underline-offset-4">{t('partnerProof')}</a>
                    </>
                  )}
                </Field>
              )}
              {item.attendee?.specialty && <Field label={t('specialty')}>{item.attendee.specialty}</Field>}
              {item.attendee?.professionalRegistration && <Field label={t('registration')}>{item.attendee.professionalRegistration}</Field>}
            </>
          )}
          <Field label={t('price')} mono>{formatPrice(record.grossCents)}</Field>
          {record.coupon && <Field label={t('coupon')} mono>{`${record.coupon.code} (−${formatPrice(record.coupon.discountCents)})`}</Field>}
          {record.installments !== null && record.installments > 1 && <Field label={t('installments')}>{record.installments}</Field>}
          <Field label={t('createdAt')}>{date(record.createdAt)}</Field>
          {record.paymentStatus === 'pending' && <Field label={t('expiresAt')}>{date(record.expiresAt)}</Field>}
          {record.completedAt && <Field label={t('completedAt')}>{date(record.completedAt)}</Field>}
          {record.failureReason && <Field label={t('failureReason')}>{failureLabel[record.failureReason] ?? record.failureReason}</Field>}
          {record.refundedAt && <Field label={t('refundedAt')}>{date(record.refundedAt)}</Field>}
          {record.refundReason && <Field label={t('refundReason')}>{record.refundReason}</Field>}
          <Field label={t('id')} mono>{record.id}</Field>
        </dl>
      </CardContent>
    </Card>
  )
}
